import {
  calendarFromServiceLevels,
  DEFAULT_PRAYER_PLACE,
  isRamadan,
  prayerTimes,
  workingIntervals,
  type PrayerMethod,
  type PrayerName,
  type ServiceLevels,
} from '@ly360/i18n';
import { ErrorState, Loading, Select, TimeField, useFormat } from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check } from 'lucide-react';
import { Link } from 'react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from '../crm/forms';
import { api } from '../lib/api';

interface Holiday {
  id: string;
  date: string;
  name: string;
  nameAr: string | null;
}
interface Payload {
  serviceLevels: ServiceLevels;
  holidays: Holiday[];
}

const PRAYERS: PrayerName[] = ['dhuhr', 'asr', 'maghrib', 'isha'];
const METHODS: PrayerMethod[] = ['umm_al_qura', 'uae', 'mwl', 'karachi', 'kuwait', 'qatar'];
const KEY = ['service-levels'];

/** Local "YYYY-MM-DD" for today + n days in a zone. */
function localDay(zone: string, plusDays: number) {
  const d = new Date(Date.now() + plusDays * 86_400_000);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}
/** A date-only value as an instant that is the same calendar day in every zone within ±12 h. */
const noonOf = (date: string) => new Date(`${date}T12:00:00Z`);

export function ServiceLevelsSettings() {
  const q = useQuery({ queryKey: KEY, queryFn: () => api<Payload>('/v1/tenant/service-levels') });
  if (q.isLoading) return <Loading />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  return (
    <>
      <LevelsForm initial={q.data.serviceLevels} holidays={q.data.holidays} />
      <p className="faint" style={{ fontSize: 'var(--t-xs)' }}>
        <HolidaysLink />
      </p>
    </>
  );
}

function LevelsForm({ initial, holidays }: { initial: ServiceLevels; holidays: Holiday[] }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [f, setF] = useState(initial);
  useEffect(() => setF(initial), [initial]);
  const save = useMutation({
    mutationFn: (v: ServiceLevels) =>
      api<Payload>('/v1/tenant/service-levels', {
        method: 'PUT',
        body: JSON.stringify({
          firstResponseMinutes: v.firstResponseMinutes,
          businessHours: v.businessHours,
          autoAssign: v.autoAssign,
          ramadanHours: v.ramadanHours,
          prayerPauses: v.prayerPauses,
        }),
      }),
    onSuccess: (r) => qc.setQueryData(KEY, r),
  });
  const p = f.prayerPauses;
  const setP = (patch: Partial<ServiceLevels['prayerPauses']>) =>
    setF({ ...f, prayerPauses: { ...p, ...patch } });
  const place = DEFAULT_PRAYER_PLACE[f.country] ?? DEFAULT_PRAYER_PLACE.AE!;
  const ar = i18n.language === 'ar' || i18n.language === 'ur';

  return (
    <section className="card card-pad" aria-labelledby="sla-title" data-testid="service-levels">
      <h2 id="sla-title" className="section-title">
        {t('sla.title')}
      </h2>
      <p className="faint" style={{ marginTop: 0, fontSize: 'var(--t-xs)' }}>
        {t('sla.hint', { zone: f.timezone })}
      </p>
      <div className="form-grid">
        <Field id="sla-target" label={t('sla.firstResponse')} hint={t('sla.firstResponseHint')}>
          <input
            id="sla-target"
            className="input"
            type="number"
            min={1}
            max={10080}
            value={f.firstResponseMinutes}
            onChange={(e) => setF({ ...f, firstResponseMinutes: Number(e.target.value) })}
          />
        </Field>
        <Field id="sla-open" label={t('sla.opens')}>
          <TimeField
            id="sla-open"
            value={f.businessHours.start}
            onChange={(v) => setF({ ...f, businessHours: { ...f.businessHours, start: v } })}
          />
        </Field>
        <Field id="sla-close" label={t('sla.closes')}>
          <TimeField
            id="sla-close"
            value={f.businessHours.end}
            onChange={(v) => setF({ ...f, businessHours: { ...f.businessHours, end: v } })}
          />
        </Field>
      </div>
      <label className="switch">
        <span>{t('sla.autoAssign')}</span>
        <input
          type="checkbox"
          checked={f.autoAssign}
          onChange={(e) => setF({ ...f, autoAssign: e.target.checked })}
        />
      </label>

      <label className="switch">
        <span>
          {t('sla.ramadan')}
          <span className="hint" style={{ display: 'block' }}>
            {t('sla.ramadanHint')}
          </span>
        </span>
        <input
          type="checkbox"
          data-testid="ramadan-toggle"
          checked={!!f.ramadanHours}
          onChange={(e) =>
            setF({ ...f, ramadanHours: e.target.checked ? { start: '09:00', end: '15:00' } : null })
          }
        />
      </label>
      {f.ramadanHours && (
        <div className="form-grid">
          <Field id="sla-r-open" label={t('sla.ramadanOpens')}>
            <TimeField
              id="sla-r-open"
              value={f.ramadanHours.start}
              onChange={(v) => setF({ ...f, ramadanHours: { ...f.ramadanHours!, start: v } })}
            />
          </Field>
          <Field id="sla-r-close" label={t('sla.ramadanCloses')}>
            <TimeField
              id="sla-r-close"
              value={f.ramadanHours.end}
              onChange={(v) => setF({ ...f, ramadanHours: { ...f.ramadanHours!, end: v } })}
            />
          </Field>
        </div>
      )}

      <label className="switch">
        <span>
          {t('sla.prayer')}
          <span className="hint" style={{ display: 'block' }}>
            {t('sla.prayerHint')}
          </span>
        </span>
        <input
          type="checkbox"
          data-testid="prayer-toggle"
          checked={p.enabled}
          onChange={(e) => setP({ enabled: e.target.checked })}
        />
      </label>
      {p.enabled && (
        <div style={{ display: 'grid', gap: '0.75rem', paddingTop: '0.75rem' }}>
          <fieldset className="chip-set">
            <legend>{t('sla.prayers')}</legend>
            {PRAYERS.map((name) => (
              <label key={name} className="chip chip-toggle">
                <input
                  type="checkbox"
                  checked={p.prayers.includes(name)}
                  onChange={(e) =>
                    setP({
                      prayers: e.target.checked ? [...p.prayers, name] : p.prayers.filter((x) => x !== name),
                    })
                  }
                />
                {t(`sla.prayer_${name}`)}
              </label>
            ))}
          </fieldset>
          <div className="form-grid">
            <Field id="sla-pause" label={t('sla.pauseMinutes')}>
              <input
                id="sla-pause"
                className="input"
                type="number"
                min={5}
                max={60}
                value={p.minutes}
                onChange={(e) => setP({ minutes: Number(e.target.value) })}
              />
            </Field>
            <Field id="sla-method" label={t('sla.method')}>
              <Select
                id="sla-method"
                value={p.method ?? ''}
                onChange={(v) => setP({ method: (v || null) as PrayerMethod | null })}
                options={[
                  { value: '', label: t('sla.methodDefault', { method: t(`sla.methods.${place.method}`) }) },
                  ...METHODS.map((m) => ({ value: m, label: t(`sla.methods.${m}`) })),
                ]}
              />
            </Field>
            <Field id="sla-asr" label={t('sla.asr')}>
              <Select
                id="sla-asr"
                value={p.asr}
                onChange={(v) => setP({ asr: v as 'standard' | 'hanafi' })}
                options={[
                  { value: 'standard', label: t('sla.asrStandard') },
                  { value: 'hanafi', label: t('sla.asrHanafi') },
                ]}
              />
            </Field>
            <Field
              id="sla-lat"
              label={t('sla.latitude')}
              hint={t('sla.locationHint', { latitude: place.latitude, longitude: place.longitude })}
            >
              <input
                id="sla-lat"
                className="input"
                dir="ltr"
                inputMode="decimal"
                value={p.latitude ?? ''}
                placeholder={String(place.latitude)}
                onChange={(e) => setP({ latitude: e.target.value === '' ? null : Number(e.target.value) })}
              />
            </Field>
            <Field id="sla-lng" label={t('sla.longitude')}>
              <input
                id="sla-lng"
                className="input"
                dir="ltr"
                inputMode="decimal"
                value={p.longitude ?? ''}
                placeholder={String(place.longitude)}
                onChange={(e) => setP({ longitude: e.target.value === '' ? null : Number(e.target.value) })}
              />
            </Field>
          </div>
        </div>
      )}

      <WeekPreview levels={f} holidays={holidays} ar={ar} />

      {save.isError && <ErrorState error={save.error} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: '1rem' }}>
        <button
          className="btn btn-grad"
          onClick={() => save.mutate(f)}
          disabled={save.isPending}
          data-testid="save-sla"
        >
          {save.isPending ? t('common.saving') : t('common.save')}
        </button>
        {save.isSuccess && (
          <span
            className="faint"
            role="status"
            style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
          >
            <Check size={16} color="var(--success)" /> {t('settings.saved')}
          </span>
        )}
      </div>
    </section>
  );
}

/** What the clock will actually do this week, computed with the same code the server uses. */
function WeekPreview({ levels, holidays, ar }: { levels: ServiceLevels; holidays: Holiday[]; ar: boolean }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const days = useMemo(() => {
    const valid =
      /^\d\d:\d\d$/.test(levels.businessHours.start) && /^\d\d:\d\d$/.test(levels.businessHours.end);
    if (!valid) return [];
    const cal = calendarFromServiceLevels(
      levels,
      holidays.map((h) => h.date),
    );
    return Array.from({ length: 7 }, (_, i) => {
      const date = localDay(levels.timezone, i);
      const holiday = holidays.find((h) => h.date === date);
      return {
        date,
        holiday,
        intervals: workingIntervals(date, cal),
        ramadan: isRamadan(noonOf(date), levels.timezone),
      };
    });
  }, [levels, holidays]);
  const today = days[0];
  const prayers =
    levels.prayerPauses.enabled && today
      ? (() => {
          const place = DEFAULT_PRAYER_PLACE[levels.country] ?? DEFAULT_PRAYER_PLACE.AE!;
          const p = levels.prayerPauses;
          return prayerTimes(
            today.date,
            { latitude: p.latitude ?? place.latitude, longitude: p.longitude ?? place.longitude },
            p.method ?? place.method,
            p.asr,
            { ramadan: today.ramadan },
          );
        })()
      : null;

  return (
    <div className="sla-preview" data-testid="sla-preview">
      <h3 className="section-title">{t('sla.preview')}</h3>
      <p className="faint" style={{ margin: '0 0 0.5rem', fontSize: 'var(--t-xs)' }}>
        {t('sla.previewHint', { zone: fmt.zoneCaption })}
      </p>
      {prayers && (
        <p className="sla-prayers" data-testid="prayer-times">
          <strong>{t('sla.todaysPrayers')}</strong>{' '}
          {PRAYERS.map((n) => `${t(`sla.prayer_${n}`)} ${fmt.time(prayers[n])}`).join(' · ')}
        </p>
      )}
      <ul className="list-plain sla-days">
        {days.map((d) => (
          <li key={d.date}>
            <span className="sla-day">
              {fmt.date(noonOf(d.date))}
              {d.ramadan && <span className="badge badge-neutral">{t('sla.ramadanBadge')}</span>}
            </span>
            {d.holiday ? (
              <span className="sla-hours" dir="auto">
                {(ar && d.holiday.nameAr) || d.holiday.name}
              </span>
            ) : (
              <span className="sla-hours" dir={d.intervals.length ? 'ltr' : undefined}>
                {d.intervals.length
                  ? d.intervals.map((iv) => `${fmt.time(iv.start)}–${fmt.time(iv.end)}`).join(', ')
                  : t('sla.closed')}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function HolidaysLink() {
  const { t } = useTranslation();
  return <Link to="/calendar">{t('sla.manageHolidays')}</Link>;
}
